// Взаимозамените на листа „Инвентаризация" — действията на секцията вдясно
// (избор на донор, „Замени", „Отмени") и кои редове носят чипа ⇄. Приложените
// двойки (получател → донор) живеят в страницата, защото от тях зависят и
// редовете (нетирането), и черновата, и зареждането при редакция; сметките са
// в `stocktakeWorksheetShared`.
import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import {
  DIFF_EPSILON,
  appliedDonorOf,
  donorOf,
  substitutionAvailability,
  type SubstitutionProposal,
} from "./stocktakeWorksheetShared";

type Params = {
  proposals: SubstitutionProposal[];
  appliedDonorByRecipient: Map<number, number>;
  setAppliedDonorByRecipient: Dispatch<SetStateAction<Map<number, number>>>;
  onTouch: () => void; // вдига „незапазени промени" + чернова
};

export const useStocktakeSubstitutions = ({
  proposals,
  appliedDonorByRecipient,
  setAppliedDonorByRecipient,
  onTouch,
}: Params) => {
  // получател → донор, избран в падащото меню (и преди прилагане)
  const [selectedDonorByRecipient, setSelectedDonorByRecipient] = useState<
    Map<number, number>
  >(new Map());

  // Всички участници в предложенията (чип ⇄) и само приложените (плътен чип)
  const substitutionProductIds = useMemo(() => {
    const ids = new Set<number>();
    for (const proposal of proposals) {
      ids.add(proposal.recipientProductId);
      for (const donor of proposal.donors) ids.add(donor.productId);
    }
    return ids;
  }, [proposals]);
  const appliedProductIds = useMemo(() => {
    const ids = new Set<number>();
    for (const proposal of proposals) {
      const donor = appliedDonorOf(proposal, appliedDonorByRecipient);
      if (!donor) continue;
      ids.add(proposal.recipientProductId);
      ids.add(donor.productId);
    }
    return ids;
  }, [proposals, appliedDonorByRecipient]);

  const applySubstitution = (recipientProductId: number) => {
    const proposal = proposals.find((p) => p.recipientProductId === recipientProductId);
    if (!proposal) return;
    const donor = donorOf(proposal, selectedDonorByRecipient.get(recipientProductId));
    if (!donor) return;
    // Излишъкът на донора може вече да е изяден от друга приложена замяна
    const { donorRemaining } = substitutionAvailability(proposals, appliedDonorByRecipient);
    if ((donorRemaining.get(donor.productId) ?? 0) <= DIFF_EPSILON) return;
    const donorId = donor.productId;
    setAppliedDonorByRecipient((prev) => new Map(prev).set(recipientProductId, donorId));
    onTouch();
  };
  const revertSubstitution = (recipientProductId: number) => {
    setAppliedDonorByRecipient((prev) => {
      const next = new Map(prev);
      next.delete(recipientProductId);
      return next;
    });
    onTouch();
  };
  const selectDonor = (recipientProductId: number, donorProductId: number) =>
    setSelectedDonorByRecipient((prev) => new Map(prev).set(recipientProductId, donorProductId));

  return {
    substitutionProductIds,
    appliedProductIds,
    selectedDonorByRecipient,
    selectDonor,
    applySubstitution,
    revertSubstitution,
  };
};
